/**
 * FeatureSectionTabs — Web Component
 *
 * Autoplaying tab interface with:
 * - CSS-animated progress bar (no GSAP)
 * - JS-measured height animation for tab body (accordion pattern)
 * - IntersectionObserver-driven video playback (dispatched from inline script in .liquid)
 * - Shopify theme editor support
 *
 * Autoplay is intentionally NOT paused on hover — it only pauses when the
 * section scrolls out of view.
 *
 * Autoplay timing is driven by animationend on the progress-fill element so the
 * tab advance fires exactly when the CSS bar completes — no drift between the
 * visual and the timer. A safety-net setTimeout (+100 ms) fires if animationend
 * is suppressed (e.g. animation already completed before the listener attached).
 */

class FeatureSectionTabs extends HTMLElement {
  constructor() {
    super();
    // Bind handlers so they can be removed in disconnectedCallback
    this._onTabClick = this._onTabClick.bind(this);
    this._onViewportChange = this._onViewportChange.bind(this);
    this._onMuteBtnClick = this._onMuteBtnClick.bind(this);
    this._onResize = this._debounce(this._onResize.bind(this), 150);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    this._removeListeners();
    window.removeEventListener('resize', this._onResize);
  }

  init() {
    this.tabs = Array.from(this.querySelectorAll('.feature-section-tabs__tab'));
    this.mediaSlides = Array.from(this.querySelectorAll('.feature-section-tabs__media-slide'));
    this.tabBodyWrappers = Array.from(this.querySelectorAll('.feature-section-tabs__tab-body-wrapper'));
    this.tabBodies = Array.from(this.querySelectorAll('.feature-section-tabs__tab-body'));

    if (this.tabs.length === 0) {
      this.classList.add('feature-section-tabs--resolved-empty');
      return;
    }
    this.classList.remove('feature-section-tabs--resolved-empty');

    this.autoplayDuration = parseInt(this.dataset.autoplay, 10) || 5000;
    this.autoplayEnabled = this.dataset.autoplayEnabled !== 'false';
    this.currentIndex = 0;
    this.isInViewport = this.getAttribute('data-in-viewport') === 'true';
    this._pendingAnimEnd = null;
    this.autoplayTimer = null;

    // Close all body wrappers, then open the first
    this.tabBodyWrappers.forEach((wrapper) => {
      wrapper.style.height = '0px';
    });
    this._openTabBody(0);

    this._removeListeners();
    this._addListeners();

    if (this.tabs.length > 1 && this.autoplayEnabled) {
      this._startAutoplay();
    }

    if (this.isInViewport) {
      this._playActiveVideo();
    }
  }

  // --------------------------------------------------
  // Listeners
  // --------------------------------------------------

  _addListeners() {
    this.tabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));
    this.addEventListener('fst:viewport-change', this._onViewportChange);
    this.addEventListener('click', this._onMuteBtnClick);
    window.addEventListener('resize', this._onResize);
  }

  _removeListeners() {
    this.tabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
    this.removeEventListener('fst:viewport-change', this._onViewportChange);
    this.removeEventListener('click', this._onMuteBtnClick);
  }

  // --------------------------------------------------
  // Tab activation
  // --------------------------------------------------

  activateTab(newIndex) {
    if (newIndex < 0 || newIndex >= this.tabs.length) return;

    const prevIndex = this.currentIndex;
    this.currentIndex = newIndex;

    // Update tab buttons
    this.tabs.forEach((tab, i) => {
      const active = i === newIndex;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Update media slides
    this.mediaSlides.forEach((slide, i) => {
      const active = i === newIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    // Animate body text heights
    if (prevIndex !== newIndex) {
      this._closeTabBody(prevIndex);
    }
    this._openTabBody(newIndex);

    // Switch videos
    this._pauseAllVideos();
    if (this.isInViewport) {
      this._playActiveVideo();
    }

    // Restart CSS progress bar animation on the new active tab
    const fill = this.tabs[newIndex].querySelector('.feature-section-tabs__progress-fill');
    if (fill) {
      // Remove animation, force reflow, restore — this restarts the keyframe from 0
      fill.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      fill.offsetHeight; // triggers reflow — DO NOT REMOVE
      fill.style.animation = '';
    }
  }

  // --------------------------------------------------
  // Body height animation (accordion pattern, matches custom-text-accordion.js)
  // --------------------------------------------------

  _openTabBody(index) {
    const wrapper = this.tabBodyWrappers[index];
    const body = this.tabBodies[index];
    if (!wrapper || !body) return;
    // Temporarily set to auto to measure natural height
    wrapper.style.height = 'auto';
    const h = body.offsetHeight;
    // Set back to current height (0) then to target for transition
    wrapper.style.height = '0px';
    // Force reflow so transition fires
    wrapper.offsetHeight; // eslint-disable-line no-unused-expressions
    wrapper.style.height = h + 'px';
  }

  _closeTabBody(index) {
    const wrapper = this.tabBodyWrappers[index];
    if (!wrapper) return;
    wrapper.style.height = '0px';
  }

  // --------------------------------------------------
  // Autoplay
  // --------------------------------------------------

  // Autoplay only pauses when the section is scrolled out of view.
  _isBlocked() {
    return !this.isInViewport;
  }

  _startAutoplay() {
    this._stopAutoplay();
    if (this._isBlocked() || this.tabs.length <= 1) return;

    // Shared advance callback — called by either animationend or the fallback timer.
    // _stopAutoplay() inside it cancels whichever of the two hasn't fired yet.
    const advance = () => {
      this._stopAutoplay();
      if (this._isBlocked()) return;
      const nextIndex = (this.currentIndex + 1) % this.tabs.length;
      this.activateTab(nextIndex);
      this._startAutoplay();
    };

    // Primary: fire exactly when the CSS progress bar finishes.
    const fill = this.tabs[this.currentIndex]?.querySelector('.feature-section-tabs__progress-fill');
    if (fill) {
      fill.addEventListener('animationend', advance, { once: true });
      this._pendingAnimEnd = { fill, handler: advance };
    }

    // Safety net: if animationend is suppressed (animation already done, element
    // hidden, etc.) advance after the full duration + a small buffer.
    this.autoplayTimer = setTimeout(advance, this.autoplayDuration + 100);
  }

  _stopAutoplay() {
    if (this._pendingAnimEnd) {
      const { fill, handler } = this._pendingAnimEnd;
      fill.removeEventListener('animationend', handler);
      this._pendingAnimEnd = null;
    }
    if (this.autoplayTimer !== null) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  // --------------------------------------------------
  // Video management
  // --------------------------------------------------

  _playActiveVideo() {
    const activeSlide = this.mediaSlides[this.currentIndex];
    if (!activeSlide) return;
    const video = activeSlide.querySelector('video');
    if (!video) return;
    const promise = video.play();
    if (promise !== undefined) {
      promise.catch(() => {
        // Autoplay blocked — silently ignore (poster will remain visible)
      });
    }
  }

  _pauseAllVideos() {
    this.querySelectorAll('video').forEach((v) => v.pause());
  }

  // --------------------------------------------------
  // Event handlers
  // --------------------------------------------------

  _onTabClick(event) {
    const tab = event.currentTarget;
    const index = parseInt(tab.dataset.tabIndex, 10);
    if (isNaN(index) || index === this.currentIndex) return;
    this._stopAutoplay();
    this.activateTab(index);
    if (this.tabs.length > 1 && this.autoplayEnabled) this._startAutoplay();
  }

  _onViewportChange(event) {
    this.isInViewport = event.detail.visible;
    if (this.isInViewport) {
      this._startAutoplay();
      this._playActiveVideo();
    } else {
      this._stopAutoplay();
      this._pauseAllVideos();
    }
  }

  _onResize() {
    // Re-measure the active body wrapper in case text has reflowed
    this._openTabBody(this.currentIndex);
  }

  _onMuteBtnClick(event) {
    const btn = event.target.closest('.feature-section-tabs__mute-btn');
    if (!btn) return;
    const slide = btn.closest('.feature-section-tabs__media-slide');
    if (!slide) return;
    const video = slide.querySelector('video');
    if (!video) return;
    const nowMuted = !video.muted;
    video.muted = nowMuted;
    btn.setAttribute('data-muted', nowMuted ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      nowMuted
        ? btn.getAttribute('data-label-muted') || 'Unmute video'
        : btn.getAttribute('data-label-unmuted') || 'Mute video'
    );
    if (!nowMuted && video.paused) video.play().catch(() => {});
  }

  // --------------------------------------------------
  // Utility
  // --------------------------------------------------

  _debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('feature-section-tabs', FeatureSectionTabs);

// --------------------------------------------------
// Shopify theme editor hooks
// --------------------------------------------------

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const component = event.target.querySelector('feature-section-tabs');
    if (component) component.init();
  });

  // Highlight the corresponding tab when a block is selected in the editor
  document.addEventListener('shopify:block:select', (event) => {
    const component = event.target.closest('feature-section-tabs');
    if (!component) return;
    const tab = event.target.closest('.feature-section-tabs__tab');
    if (!tab) return;
    const index = parseInt(tab.dataset.tabIndex, 10);
    if (!isNaN(index)) {
      component._stopAutoplay();
      component.activateTab(index);
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const component = event.target.closest('feature-section-tabs');
    if (!component) return;
    if (component.tabs.length > 1) component._startAutoplay();
  });
}
